import React from "react";

export function Terms() {
  return (
    <div className="legal-page">
      <article className="legal-content">
        <h1>Terms of Service</h1>
        <p className="legal-updated">Last updated: April 4, 2026</p>
        <p>
          These Terms of Service (&quot;Terms&quot;) govern your access to and use
          of the NumisTrail platform, applications, and services (the
          &quot;Service&quot;), operated by Storychain Labs LLC, an Oregon limited
          liability company, doing business as NumisTrail (&quot;NumisTrail,&quot;
          &quot;we,&quot; &quot;our,&quot; or &quot;us&quot;).
        </p>
        <p>
          By accessing or using the Service, you agree to be bound by these Terms
          and the Privacy Policy.
        </p>

        <ol className="legal-list">
          <li>
            <h2>Description of the Service</h2>
            <p>
              NumisTrail provides a digital platform for creating and managing
              blockchain-based digital records (&ldquo;Digital Records&rdquo; or
              &ldquo;NFTs (digital items)&rdquo;) associated with certified collectibles.
            </p>
            <p>
              Digital Records may be identified as
              &ldquo;TRUSTED_CERTIFIED&rdquo; or &ldquo;USER_GENERATED.&rdquo;
              USER_GENERATED records are submitted by collectors and are not
              digitally certified by a trusted partner at creation time.
            </p>
            <p>
              To protect platform integrity and comply with legal, operational,
              and security obligations, NumisTrail may review and take action on
              USER_GENERATED records, including modifying metadata, removing a
              record, or reassigning a record within the Service when required.
            </p>
            <p>
              These operational actions apply only to the digital record in
              NumisTrail and do not transfer physical custody, legal title, or
              possession of any physical collectible.
            </p>
            <p>
              A USER_GENERATED record may later be promoted to
              TRUSTED_CERTIFIED when an authorized trusted partner performs a
              digital certification action on the platform. Such promotion
              reflects platform metadata and event history only and does not
              transfer physical custody or legal title.
            </p>
            <p>
              Ownership as referenced in the Service refers solely to ownership
              of the Digital Record within the NumisTrail platform and/or
              associated blockchain systems. Digital Records represent metadata,
              provenance, and verification information only and do not constitute
              ownership, custody, or control of any physical collectible.
            </p>
            <p>
              Ownership, possession, transfer, and storage of physical
              collectibles occur entirely outside the Service and are the sole
              responsibility of the relevant parties.
            </p>
          </li>
          <li>
            <h2>Eligibility &amp; Geographic Scope</h2>
            <p>
              You must be at least 18 years old and legally capable of entering
              into binding agreements to use the Service.
            </p>
            <p>
              The Service is offered internationally. You are responsible for
              complying with the laws and regulations that apply to you.
            </p>
            <p>
              You may not use the Service where such use is prohibited by
              applicable laws or regulations, including U.S. export controls,
              economic sanctions, or trade restrictions. By using the Service,
              you represent that you are not subject to any applicable sanctions
              or restrictions that would prohibit your use of the Service.
            </p>
            <p>
              NumisTrail reserves the right to restrict, suspend, or terminate
              access to the Service as necessary to comply with applicable laws
              or regulations.
            </p>
          </li>
          <li>
            <h2>Accounts</h2>
            <p>
              You are responsible for maintaining the confidentiality of your
              credentials and for all activity under your account.
            </p>
            <p>
              If a certification entity assigns you a Digital Record and you do
              not yet have an account, NumisTrail may create a pending record
              and send you a claim email. Your account is not active until you
              complete registration and accept the legal terms.
            </p>
          </li>
          <li>
            <h2>Privacy &amp; Data Protection</h2>
            <p>
              Our Privacy Policy explains how we collect, use, and share personal
              data. By using the Service, you consent to our Privacy Policy.
            </p>
            <p>
              If you are located in the European Union, European Economic Area,
              Switzerland, or the United Kingdom, additional rights may apply as
              described in the Privacy Policy.
            </p>
          </li>
          <li>
            <h2>Ownership History &amp; Visibility</h2>
            <p>
              Ownership history associated with a Digital Record is visible by
              default only to the current owner of the associated item.
            </p>
            <p>
              Only the current owner may designate an item as public or private.
            </p>
            <p>
              If an item is designated as public, ownership history, including
              associated usernames, may become visible to other users of the
              Service.
            </p>
            <p>
              Ownership history and transaction records may be permanent once
              recorded and may not be removable due to system or blockchain
              constraints. Reverting an item to private limits future visibility
              but does not guarantee removal of historical records.
            </p>
            <p>
              NumisTrail may, at its discretion, offer pseudonymization or other
              privacy features in the future; however, such features are not
              guaranteed and may not affect data recorded on public blockchains.
            </p>
          </li>
          <li>
            <h2>User Content</h2>
            <p>
              You are responsible for content you submit to the Service,
              including notes, descriptions, and metadata. You grant NumisTrail a
              limited license to host, process, and display such content to
              operate the Service.
            </p>
            <p>
              You represent that submitted certification metadata is accurate to
              the best of your knowledge and that you will not impersonate a
              certification entity, intermediary, marketplace, or other person.
            </p>
          </li>
          <li>
            <h2>Acceptable Use</h2>
            <p>
              You agree not to misuse the Service, attempt unauthorized access,
              interfere with its operation, or use the Service for unlawful
              activity.
            </p>
            <p>
              You may not intentionally submit false, misleading, or fraudulent
              certification identifiers or provenance data.
            </p>
          </li>
          <li>
            <h2>Early-Stage &amp; &quot;As-Is&quot; Service</h2>
            <p>
              The Service is provided on an &quot;as is&quot; and &quot;as
              available&quot; basis. NumisTrail is an early-stage platform and
              may modify, suspend, or discontinue features at any time without
              notice.
            </p>
          </li>
          <li>
            <h2>Service Interruption &amp; Discontinuation</h2>
            <p>
              NumisTrail may suspend or discontinue the Service, in whole or in
              part, at any time. NumisTrail shall not be liable for losses
              arising from interruption or discontinuation.
            </p>
          </li>
          <li>
            <h2>Blockchain &amp; Third-Party Risks</h2>
            <p>
              The Service depends on third-party technologies, including
              blockchain networks, wallet providers, and cloud infrastructure.
              NumisTrail is not responsible for failures, outages, protocol
              changes, or third-party actions beyond its control.
            </p>
          </li>
          <li>
            <h2>Fees</h2>
            <p>
              Core features may currently be offered at no charge. NumisTrail
              may introduce paid features, paid tiers, or subscription plans at
              any time.
            </p>
            <p>
              If a feature that is currently free becomes paid, we will provide
              notice before charges apply to continued use of that feature.
            </p>
            <p>
              Prices, billing terms, and renewal cadence for any paid plan will
              be shown before purchase. Unless otherwise stated, subscriptions
              renew automatically at the end of each billing period.
            </p>
            <p>
              We may change pricing for future billing periods by providing
              advance notice before the updated price takes effect at renewal.
            </p>
            <p>
              You may cancel a paid subscription before renewal to avoid future
              charges. Except as required by law, fees already paid are
              non-refundable.
            </p>
            <p>
              You are responsible for applicable taxes, duties, or similar
              governmental charges related to paid use of the Service.
            </p>
          </li>
          <li>
            <h2>Intellectual Property</h2>
            <p>
              All intellectual property associated with the Service is owned by
              or licensed to Storychain Labs LLC.
            </p>
          </li>
          <li>
            <h2>Third-Party Reference Data Attribution</h2>
            <p>
              Certain numismatic reference data is provided by Nomisma.org and
              is licensed under Creative Commons Attribution (CC BY), including
              CC BY 4.0 where applicable. NumisTrail may adapt or extend this
              data for platform use.
            </p>
            <p>
              Nomisma.org data: © Nomisma.org contributors, CC BY (
              <a href="https://nomisma.org/" target="_blank" rel="noopener noreferrer">
                https://nomisma.org/
              </a>
              ).
            </p>
            <p>
              Unless specified otherwise by Nomisma.org or a contributing
              dataset, Nomisma.org content is generally published under CC BY
              3.0, and dataset-specific license notices control where they
              differ.
            </p>
          </li>
          <li>
            <h2>Suspension &amp; Termination</h2>
            <p>
              NumisTrail may suspend or terminate accounts for violations of
              these Terms or misuse of the Service.
            </p>
          </li>
          <li>
            <h2>Disclaimer of Warranties</h2>
            <p>
              The Service is provided without warranties of any kind, express or
              implied.
            </p>
          </li>
          <li>
            <h2>Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, NumisTrail shall not be
              liable for indirect, incidental, or consequential damages.
            </p>
          </li>
          <li>
            <h2>Governing Law &amp; Jurisdiction</h2>
            <p>
              These Terms are governed by the laws of the State of Oregon,
              United States, and disputes shall be resolved exclusively in
              Oregon courts.
            </p>
            <p>
              If you are a consumer in the European Union, European Economic
              Area, Switzerland, or the United Kingdom, you may have mandatory
              rights under local law and you may bring claims in your local
              courts as permitted by law.
            </p>
          </li>
          <li>
            <h2>Language</h2>
            <p>
              These Terms are written in English, which shall be the controlling
              language. Any translations that may be provided are for convenience
              only and have no legal effect.
            </p>
          </li>
          <li>
            <h2>Changes</h2>
            <p>
              We may update these Terms. We will provide notice of material
              changes, and continued use constitutes acceptance. We may require
              you to accept updated Terms to continue using the Service.
            </p>
          </li>
          <li>
            <h2>Contact</h2>
            <p>legal@numistrail.com</p>
          </li>
        </ol>
        <p className="legal-brand-notice">
          2026 NumisTrail (TM). A brand operated by Storychain Labs LLC.
        </p>
        <p className="legal-brand-notice">
          All third-party trademarks and brand names are the property of their
          respective owners.
        </p>
      </article>
    </div>
  );
}
